import { Component, OnInit, Input, OnChanges, OnDestroy, Output, EventEmitter } from '@angular/core';
import { debounceTime } from 'rxjs/operators';
import { CostCenter } from 'src/shared/models/cost-center.model';
import { ExpandableListItem } from 'src/shared/models/expandable-list-item.model';
import { Constants } from 'src/shared/utilities/constants.util';
import { Facility } from 'src/shared/models/facility.model';
import { VarianceReason } from 'src/shared/models/variance-reason.model';
import { JobTitle } from 'src/shared/models/job-title.model';
import { Measure } from 'src/shared/models/measure.model';
import { Status } from 'src/shared/models/status.model';
import { User } from 'src/shared/models/user.model';
import { JobTitleService } from 'src/shared/services/job-title.service';
import { CostCenterService } from 'src/shared/services/cost-center.service';
import { FacilityService } from 'src/shared/services/facility.service';
import { MeasureService } from 'src/shared/services/measure.service';
import { UserService } from 'src/shared/services/user.service';
import { VarianceReasonService } from 'src/shared/services/variance-reason.service';
import { NgForm, ControlContainer } from '@angular/forms';
import { Subscription } from 'rxjs';
import { CostCenterConfigurationService } from 'src/shared/services/cost-center-configuration.service';
import { ShiftScheduleService } from 'src/shared/services/shift-schedule.service';
import { ShiftSchedule } from 'src/shared/models/shift-schedule.model';
import { MiscUtil } from 'src/shared/utilities/misc.util';
import { ExpandableListComponent } from 'src/shared/Baseclasses/expandable-list.component';
import { InpatientShiftRecordService } from 'src/shared/services/shift-record-inpatient.service';


@Component({
  selector: 'smt-general-tab',
  templateUrl: './general-tab.component.html',
  styleUrls: ['./general-tab.component.scss'],
  viewProviders: [{ provide: ControlContainer, useExisting: NgForm }]
})
export class GeneralTabComponent implements OnChanges, OnDestroy, OnInit {
  @Input() costCenter: CostCenter;
  @Input() hasCostCenterSlots: boolean;
  @Input() readOnly: boolean;
  @Input() currentUser: User;
  @Output() saveClicked = new EventEmitter();

  private costCenterSelectedSubscription: Subscription;
  private costCenterSaveSubscription: Subscription;

  // Tabs
  defaultVariance: VarianceReason[];
  facilities: Facility[] = [];
  jobTitleColumns: string[] = [];
  jobTitleRows: ExpandableListItem[] = [];
  jobTitles: ExpandableListItem[] = [];
  jobTitleSelections: JobTitle[] = [];
  selectedFacility: Facility;
  selectedFixedJobTitles: JobTitle[] = [];
  selectedIncludedInRatio: JobTitle[] = [];
  selectedJobTitles: JobTitle[] = [];
  selectedName: string;
  selectedNumber: string;

  selectedMeasures: Measure[] = [];
  selectedStatus: boolean;
  selectedUsers: User[] = [];
  selectedVariableJobTitles: JobTitle[] = [];
  selectedVarianceReasons: VarianceReason[] = [];
  measureColumns: string[] = [];
  measureRows: ExpandableListItem[] = [];
  measures: ExpandableListItem[] = [];
  measureSelections: Measure[] = [];
  statuses: Status[] = [{ StatusId: true, StatusName: 'Active' }, { StatusId: false, StatusName: 'Inactive' }];
  userColumns: string[] = [];
  userRows: ExpandableListItem[] = [];
  userDropdownList: ExpandableListItem[] = [];
  userSelections: User[] = [];
  varianceColumns: string[] = [];
  varianceRows: ExpandableListItem[] = [];
  variances: ExpandableListItem[] = [];
  varianceSelections: VarianceReason[] = [];
  schedules: ShiftSchedule[] = [];
  isFutureShiftBlockAvailable :boolean;

  canActivateCostCenter = false;

  hasWorkflowData: boolean; 

  readonly maximumAllowedMeasures: number = 7;

  private scheduleSubscription: Subscription;
  confirmModalActive:boolean =false;
  rowIdToDelete: number;
  selectedExpandList: ExpandableListComponent;
  jobTitlesToFilter: JobTitle[];

  constructor(
    private _costCenterService: CostCenterService,
    private _facilityService: FacilityService,
    private _jobTitleService: JobTitleService,
    private _measureService: MeasureService,
    private _userService: UserService,
    private _varianceService: VarianceReasonService,
    private _shiftScheduleService: ShiftScheduleService,
    private _costCenterConfigurationService: CostCenterConfigurationService,
    private _shiftRecordService: InpatientShiftRecordService) {
    this.scheduleSubscription = this._shiftScheduleService.schedules$.subscribe((schedules) => {
      this.schedules = schedules;
    });
  }

  ngOnChanges() {
      // If the shift schedules have already been loaded and the cost center has changed then clear out the shift schedules.
      if (MiscUtil.checkArray(this.schedules) && this.schedules.some(s => s.costCenterId != this.costCenter.id)) {
          this.schedules = [];
      }
  }

  ngOnDestroy() {
    this.costCenterSelectedSubscription?.unsubscribe();
    this.costCenterSaveSubscription?.unsubscribe();

    if (this.scheduleSubscription) {
      this.scheduleSubscription?.unsubscribe();
    }
  }
  markConfirmModalActive($event:any[]){
    if (!this.isFutureShiftBlockAvailable || this.isNewCostCenter) {
      let obj = <ExpandableListComponent>$event[0];
      let id = +$event[1];
      this.rowIdToDelete = id;
      this.confirmModalActive = true;
      this.selectedExpandList = obj;
    }
    else{
      this._costCenterConfigurationService.announceWarning(this._costCenterConfigurationService.staffingGridLockOutMessage);
    }
  }
  confirmYes(id){
    this.selectedExpandList.onRemove(id,true);
    this.confirmModalActive = false;
  }
  async ngOnInit() {
    this._costCenterConfigurationService.announceAdminActionLoading(true);
    this._costCenterConfigurationService.announceFormIsValid(false);

    await Promise.all([
      this.setFacilities(),
      this.getJobTitles(),
      this.getMeasures(),
      this.getUsers(),
      this.getVariances()
    ]);

    this.loadCostCenter();

    this.costCenterSelectedSubscription = this._costCenterConfigurationService.selected$
    .pipe(debounceTime(Constants.DebounceTime))
    .subscribe((selectedCostCenter) => {
      this.costCenter = selectedCostCenter;
      this.loadCostCenter();
    });

    this.costCenterSaveSubscription = this._costCenterConfigurationService.save$
      .pipe(debounceTime(Constants.DebounceTime))
      .subscribe((selectedTab) => {
      if (selectedTab === 'general') {
        this.saveClicked.emit();
        this.saveGeneralTab();        
      }
    });
    this._costCenterConfigurationService.isFutureShiftRecordPresent$.subscribe(futureShiftRecordFlag=>{
       this.isFutureShiftBlockAvailable = futureShiftRecordFlag;
    });
    this._costCenterConfigurationService.announceAdminActionLoading(false);
    this._costCenterConfigurationService.announceFormIsValid(this.isValid);
  }

  async loadCostCenter() {
    this._costCenterConfigurationService.announceAdminActionLoading(true);
    this._costCenterConfigurationService.announceFormIsValid(false);
    this.setSelectedCostCenterValues();
    this.setIsActiveDropdownDisabled();
    this._costCenterConfigurationService.announceAdminActionLoading(false);
    this._costCenterConfigurationService.announceFormIsValid(this.isValid);
  }

  async setFacilities() {
    this.facilities = await this._facilityService.getFacilities();
  }

  async shouldEnableActiveDropdown() : Promise<boolean> {
    // Do not check anything if we are in the "Add New" state
    if (!this.costCenter || !this.costCenter.id || !this.costCenter.costCenterType) {
      return false;
    }

    // Do not check anything if we have saved the general tab, but not the details tab
    if (!this.costCenter.ancillaryConfiguration && !this.costCenter.inpatientConfiguration) {
      return false;
    }

    // We should always be able to inactivate an active cost center
    if (!this.costCenter.inactiveDate) {
      return true;
    }

    // Check to see if we have hit our cost center limit
    if (!this.hasCostCenterSlots) {
      return false;
    }

    return await this._shiftScheduleService.checkCostCenterSchedules(this.costCenter);
  }

  async setIsActiveDropdownDisabled() {
    this.canActivateCostCenter = await this.shouldEnableActiveDropdown();
  }

  async getJobTitles() {
    this.jobTitles = [];
    this.jobTitleColumns = ["Order","Abbreviation", "Position", "Include in Ratio", "Type"];
    await this._jobTitleService.getJobTitles()
      .then((results) => {
        this.jobTitles = this.jobTitles.concat(this.jobsToExpandable(results, false));
        this.jobTitlesToFilter = results;
      });
  }

  get isNewCostCenter(): boolean{
    return !this.costCenter.id || (!this.costCenter.ancillaryConfiguration &&!this.costCenter.inpatientConfiguration);
  }

  jobChangeEvent(jobRows: ExpandableListItem[]) {

    if (!this.isFutureShiftBlockAvailable || this.isNewCostCenter) {
      if(this.selectedJobTitles===undefined){
        this.selectedJobTitles =[];
        this.selectedVariableJobTitles =[];
        this.selectedIncludedInRatio =[];
        this.selectedFixedJobTitles =[];
      }
      let idsToIgnore = this.selectedJobTitles.map(n => n.id);
      this._costCenterConfigurationService.announceFormIsDirty(true);
      if (jobRows.length > this.selectedJobTitles.length) {

        // moving the recently added job title to the top of the list
        let newRow = jobRows[jobRows.length - 1];
        newRow.order = 1;
        newRow.cells.forEach(v=>{
          if(v.type=='list'){
            v.value="";
          }
        })
        jobRows.pop();
        jobRows.splice(0, 0, newRow);

        jobRows.forEach(async (e: ExpandableListItem) => {
          if (idsToIgnore.indexOf(e.id) == -1) {
            let jobTitle = this.jobTitlesToFilter.find(j => j.id == e.id);

            if (jobTitle) {
              if (e.cells.some(c => c.id == "includedInRatio" && c.value)) {
                this.selectedIncludedInRatio.push(jobTitle);
              }

              if (e.cells.some(c => c.id == "isFixed" && c.value == "Fixed")) {
                this.selectedFixedJobTitles.push(jobTitle);
              }
              else {
                this.selectedVariableJobTitles.push(jobTitle);
              }

              this.selectedJobTitles.push(jobTitle);
            }
          }
        });
      }
      else {
        this.selectedJobTitles = this.selectedJobTitles.map(n => {
          return this.filterJobTitle(jobRows, n);
        }).filter(n=>{return n!==undefined});
    
        this.selectedIncludedInRatio = this.selectedJobTitles.map(n => {
          return this.filterJobTitle(jobRows, n,true);
        }).filter(n=>{return n!==undefined});

        this.selectedVariableJobTitles = this.selectedJobTitles.map(n => {
          return this.filterJobTitle(jobRows, n,false,false);
        }).filter(n=>{return n!==undefined});

        this.selectedFixedJobTitles = this.selectedJobTitles.map(n => {
          return this.filterJobTitle(jobRows, n,false,true);
        }).filter(n=>{return n!==undefined});

      }

      this.handleJobChangeWarningMessage();
          
      this._costCenterConfigurationService.announceFormIsValid(this.isValid);
    }
    else{
      this._costCenterConfigurationService.announceWarning(this._costCenterConfigurationService.staffingGridLockOutMessage);
      return false;
    }
  }

  jobsToExpandable(jobs: JobTitle[], isFixed: boolean): ExpandableListItem[] {
    let listItems: ExpandableListItem[] = [];

    if (!jobs) {
      return listItems;
    }

    jobs.forEach(j => {
      let listItem = new ExpandableListItem();
      let includeInRatio = this.selectedIncludedInRatio ? this.selectedIncludedInRatio.some(r => r.id == j.id) : false;

      listItem.id = j.id;
      listItem.displayValue = j.abbreviation + ":" + j.position;
      listItem.order = j.order;
      listItem.groupby =  isFixed ? "Fixed" : "Variable";
      listItem.cells = [
        { value: j.abbreviation },
        { value: j.position },
        { id: "includedInRatio", type: "checkBox", value: includeInRatio,readOnly: isFixed ? true : false },
        { id: "isFixed", type: "list", options: ["Fixed", "Variable"], value: isFixed ? "Fixed" : "Variable" }
      ];

      listItems.push(listItem);
    });

    return listItems;
  }

  async getMeasures() {
    this.measures = [];

    await this._measureService.getMeasures()
      .then((results) => {
        this.measureSelections = results;
        this.measures = this.measures.concat(this.staffingToExpandable(results));
      });
  }

    private async handleJobChangeWarningMessage() {

        if(this.selectedFixedJobTitles == null || this.selectedFixedJobTitles == undefined) {
          this.selectedFixedJobTitles =[];
        }
        if(this.costCenter.fixedJobTitles == null || this.costCenter.fixedJobTitles == undefined) {
          this.costCenter.fixedJobTitles=[];
        }
        if(this.selectedVariableJobTitles == null || this.selectedVariableJobTitles == undefined){
          this.selectedVariableJobTitles =[];
        }
        if(this.costCenter.variableJobTitles == null || this.costCenter.variableJobTitles == undefined){
          this.costCenter.variableJobTitles = [];
        }
        // Check if there were any changes to the fixed jobs on the cost center.
        let hasFixedJobTitlesChanged = (MiscUtil.checkArray(this.costCenter.fixedJobTitles) &&  
            this.costCenter.fixedJobTitles.some(cj => !this.selectedFixedJobTitles.some(fj => fj.id === cj.id))) ||
            (MiscUtil.checkArray(this.selectedFixedJobTitles) && MiscUtil.checkArray(this.costCenter.fixedJobTitles) && 
            this.selectedFixedJobTitles.some(fj => !this.costCenter.fixedJobTitles.some(cj => cj.id === fj.id)));
        
        // Check if there were any changes to the variable jobs on the cost center.
        let hasVariableJobTitlesChanged = (MiscUtil.checkArray(this.costCenter.variableJobTitles) && 
            this.costCenter.variableJobTitles.some(cj => !this.selectedVariableJobTitles.some(fj => fj.id === cj.id))) ||
            (MiscUtil.checkArray(this.selectedVariableJobTitles) && 
            this.selectedVariableJobTitles.some(fj => !this.costCenter.variableJobTitles.some(cj => cj.id === fj.id)));

        // If this is not a new cost center and has job title changes then check if a warning should be shown.
        if(!this.isNewCostCenter && (hasFixedJobTitlesChanged || hasVariableJobTitlesChanged)) {
            if (MiscUtil.checkArray(this.schedules)) {
                this._costCenterConfigurationService.announceWarning(Constants.ResetStaffingGridWarning);
            }
            else {                
                this._costCenterConfigurationService.announceValidating(true);
                this._costCenterConfigurationService.announceWarning("Please wait.  Validating jobs against staffing grid...");

                this._shiftScheduleService.getActiveShiftSchedulesByCostCenter(this.costCenter.id).then(schedules => {
                    if (MiscUtil.checkArray(schedules)) {
                        this._costCenterConfigurationService.announceWarning(Constants.ResetStaffingGridWarning);
                    }
                    else {
                        this._costCenterConfigurationService.clearWarning();
                    }
    
                    this.schedules = schedules;
                    this._costCenterConfigurationService.announceValidating(false);
                });    
            }                                    
        }    
        else {
            this._costCenterConfigurationService.clearWarning();  
        }     
    }

  staffingToExpandable(measures: Measure[]): ExpandableListItem[] {
    let listItems: ExpandableListItem[] = [];

    if (!measures) {
      return listItems;
    }

    measures.forEach(s => {
      let listItem = new ExpandableListItem();
      listItem.id = s.id;
      listItem.displayValue = s.code + ": " + s.factor;
      listItem.cells = [{ value: s.code }, { value: s.factor }];

      listItems.push(listItem);
    });

    return listItems;
  }

  async getUsers() {
    this.userDropdownList = [];
    this.userColumns = ["First Name", "Last Name", "Email"];

    await this._userService.getUsersForPermissions()
      .then((results) => {      
        this.userDropdownList = this.userDropdownList.concat(this.usersToExpandable(results,true));
        this.userSelections = results;
      });
  }

  usersToExpandable(users: User[], isDropDownLoading:boolean): ExpandableListItem[] {
    let listItems: ExpandableListItem[] = [];

    if (!users) {
      return listItems;
    }

    if(isDropDownLoading) {
      users = users.filter(u => !(this.currentUser.id == u.id));
    }
    users.forEach(u => {
      let listItem = new ExpandableListItem();
      listItem.id = u.id;
      listItem.displayValue = u.firstName + " " + u.lastName;
      listItem.cells = [{ value: u.firstName }, { value: u.lastName }, { value: u.email }];

      listItems.push(listItem);
    });

    return listItems;
  }

  async getVariances() {
    this.variances = [];
    this.varianceColumns = ["Order","Code", "Reason For Variance"];

    await this._varianceService.getVarianceReasons()
      .then((results) => {
        this.defaultVariance = results.filter(v => v.default);
        this.variances = this.variances.concat(this.reasonsToExpandable(results));
        this.varianceSelections = results;
      });
  }

  orderUpdated(orderType: string){    
    this._costCenterConfigurationService.announceFormIsDirty(true);    
    if(orderType=="Reason for Variance"){
      this.costCenter.isRFVOrderUpdated = true;
      this.selectedVarianceReasons.forEach(row=>{
        var varianceReason= this.varianceRows.find(f=>f.id==row.id);
        row.order= varianceReason.order;
      });
    }
    if(orderType==="Job Title"){
      this.costCenter.isJTOrderUpdated = true;
      this.selectedJobTitles.forEach(row=>{
        var jobTitle= this.jobTitleRows.find(f=>f.id==row.id);
        row.order= jobTitle.order;
      });
    } 
    if(orderType==="Measure"){
      this.costCenter.isMeasuresOrderUpdated = true;
      this.selectedMeasures.forEach(row=>{
        var measure= this.measureRows.find(f=>f.id==row.id);
        row.order= measure.order;
      });
    }    
  }

  reasonsChangeEvent(reasonRows: ExpandableListItem[]) {           
    if (this.selectedVarianceReasons === undefined) {
      this.selectedVarianceReasons = [];
    }
    if (reasonRows.length > this.selectedVarianceReasons.length) {
      let idsToIgnore = this.selectedVarianceReasons.map(n => n.id);
      reasonRows.forEach(async (e: ExpandableListItem) => {
        if (idsToIgnore.indexOf(e.id) == -1) {
          let varianceReason = this.varianceSelections.find(v => v.id == e.id);

          if (varianceReason) {
            varianceReason.order = this.selectedVarianceReasons.length + 1;
            this.selectedVarianceReasons.push(varianceReason);
          }
        }
      });
    }
    else{
      this.selectedVarianceReasons = this.selectedVarianceReasons.filter(r => {
        var reason = reasonRows.find(n => n.id == r.id);
        if (reason) {
          r.order = reason.order;
          return r !==undefined;
        }
      })
    }      
    this._costCenterConfigurationService.announceFormIsDirty();
    this._costCenterConfigurationService.announceFormIsValid(this.isValid);
  }

  staffingChangeEvent(staffingRows: ExpandableListItem[]) {    
            
    if (this.selectedMeasures === undefined) {
      this.selectedMeasures = [];
    }
    if (staffingRows.length > this.selectedMeasures.length) {
      let idsToIgnore = this.selectedMeasures.map(n => n.id);
      staffingRows.forEach(async (e: ExpandableListItem) => {
        if (idsToIgnore.indexOf(e.id) == -1) {
          let measure = this.measureSelections.find(v => v.id == e.id);

          if (measure) {
            measure.order = this.selectedMeasures.length + 1;
            this.selectedMeasures.push(measure);
          }
        }
      });
    }
    else{
      this.selectedMeasures = this.selectedMeasures.filter(r => {
        var measure = staffingRows.find(n => n.id == r.id);
        if (measure) {
          r.order = measure.order;
          return r !==undefined;
        }
      })
    }     

    this._costCenterConfigurationService.announceFormIsDirty(); 
    this._costCenterConfigurationService.announceFormIsValid(this.isValid);   
  }

  limitedRowsWarningMessage(exceededLimitedRows: boolean) {
    if (exceededLimitedRows) {
      this._costCenterConfigurationService.announceWarning("You cannot enter more than " + this.maximumAllowedMeasures + " measures");
    }
    else {
      this._costCenterConfigurationService.clearWarning();
    }
  }

  usersChangeEvent(users: ExpandableListItem[]) {
    this.selectedUsers = [];
    this.costCenter.isUserUpdated = true;
    users.forEach(async (e: ExpandableListItem) => {
      let user = this.userSelections.find(p => p.id == e.id);

      if (user) {
        this.selectedUsers.push(user);
      }
    });

    this._costCenterConfigurationService.announceFormIsDirty();
  }

  reasonsToExpandable(reasons: VarianceReason[]): ExpandableListItem[] {
    let listItems: ExpandableListItem[] = [];

    if (!reasons) {
      return listItems;
    }

    reasons.forEach(v => {
      let listItem = new ExpandableListItem();
      listItem.id = v.id;
      listItem.displayValue = v.code + ": " + v.reason;
      listItem.cells = [{ value: v.code }, { value: v.reason }];
      listItem.order = v.order;

      listItems.push(listItem);
    });

    return listItems;
  }

  async setSelectedCostCenterValues() {
    this.jobTitleRows = [];
    this.measureRows = [];
    this.varianceRows = [];
    this.userRows = [];

    if (this.costCenter) {
        // After each selection we assume it has workflow data so that it disables the proper controls.
        // This assumption only needs to be made if the selection has a legitimate id, which indicates
        // that it is a facility pulled from the database.
        // The controls will only enable once it has been proven to not have workflow data.
        this.hasWorkflowData = this.costCenter.id > 0;

        this.selectedFacility = this.facilities.find(f => f.id == this.costCenter.facility.id);
        this.selectedJobTitles = this.costCenter.jobTitles;
        this.selectedName = this.costCenter.name;
        this.selectedNumber = this.costCenter.number;
        this.selectedMeasures = this.costCenter.measures;
        this.selectedStatus = this.costCenter.inactiveDate == null;
        this.selectedUsers = this.costCenter.users;
        this.selectedVariableJobTitles = this.costCenter.variableJobTitles;
        this.selectedFixedJobTitles = this.costCenter.fixedJobTitles;      
        this.selectedIncludedInRatio = this.costCenter.includedInRatio;
        this.jobTitleRows = this.jobsToExpandable(this.costCenter.fixedJobTitles, true);
        this.jobTitleRows = this.jobTitleRows.concat(this.jobsToExpandable(this.costCenter.variableJobTitles, false));
        this.measureRows = this.staffingToExpandable(this.costCenter.measures);
        this.userRows = this.usersToExpandable(this.costCenter.users,false);
        this.jobTitleSelections = this.costCenter.jobTitles;

        if(!this.costCenter.id) {
            this.selectedVarianceReasons = this.defaultVariance;
            this.varianceRows = this.reasonsToExpandable(this.defaultVariance);
        }
        else{
            this.selectedVarianceReasons = this.costCenter.varianceReasons
            this.varianceRows=this.reasonsToExpandable(this.selectedVarianceReasons);
        
            // Only check the database to determine if this facility has workflow data if the id indicates
            // that this cost center exists in the DB.  There is no need to check this if the cost center is a
            // new one that hasn't been saved to the database yet.
            if (this.costCenter.id > 0) {
                this._shiftRecordService.hasCostCenterWorkflowData(this.costCenter.id).then(hasData => {
                    this.hasWorkflowData = hasData;
                });
            } 
        }
    } else {
      this.costCenter = new CostCenter();
      this.selectedFacility = this.facilities[-1];
      this.selectedJobTitles =[];
      this.selectedName = '';
      this.selectedNumber = '';
      this.costCenter.inactiveDate = new Date();
      this.selectedStatus = false;
      this.jobTitleRows = [];
      this.measureRows = [];
      this.userRows = [];
      this.varianceRows = [];
      this.selectedMeasures = [];
      this.selectedVariableJobTitles = [];
      this.selectedFixedJobTitles = [];
      this.selectedVarianceReasons = [];
      this.costCenter.varianceReasons = this.defaultVariance;
      this.hasWorkflowData = false;
    }
  }

  async saveGeneralTab() {
    if (!this.costCenter ) {
      return;
    }

    if(!this.hasCostCenterSlots) {
      this._costCenterConfigurationService.announceSaving(false);
      return;
    }

    if (!this.ValidateGeneralTab()){
      if(!this.hasMissingJobType){
      this._costCenterConfigurationService.announceError("Please correct all the errors");
      }
      this._costCenterConfigurationService.announceSaving(false);
      return;
    }

    this._costCenterConfigurationService.clearError();
    let mostRecent = this.costCenter.id == 0;
    let selectedCostCenter = this.costCenter;

    selectedCostCenter.id = this.costCenter.id;
    selectedCostCenter.name = this.selectedName;
    selectedCostCenter.number = this.selectedNumber;
    selectedCostCenter.facility = this.selectedFacility;
    selectedCostCenter.inactiveDate = selectedCostCenter.inactiveDate;
    selectedCostCenter.jobTitles = this.selectedJobTitles;
    selectedCostCenter.variableJobTitles = this.selectedVariableJobTitles;
    selectedCostCenter.fixedJobTitles = this.selectedFixedJobTitles;
    selectedCostCenter.measures = this.selectedMeasures;
    selectedCostCenter.users = this.selectedUsers    
    selectedCostCenter.varianceReasons = this.selectedVarianceReasons;
    selectedCostCenter.includedInRatio = this.selectedIncludedInRatio;
    
    if (this.selectedStatus) {
      selectedCostCenter.inactiveDate = null;
    }
    else {
      selectedCostCenter.inactiveDate = new Date();
    }

    this._costCenterConfigurationService.announceSaving(true);
    selectedCostCenter = await this._costCenterService.setCostCenter(selectedCostCenter, false);
   
    if (selectedCostCenter.id > 0 && !selectedCostCenter.errorMessage) {
        this._costCenterConfigurationService.announceError();     
        this._costCenterConfigurationService.announceFormIsDirty(false);   
        this._costCenterConfigurationService.setSelected(await this._costCenterService.getCostCenter(selectedCostCenter.id));
        this._costCenterConfigurationService.announceAdminListReload(mostRecent);
    } 
    else {
      this._costCenterConfigurationService.announceCostCenterSaveError(selectedCostCenter);
    }
        
    this._costCenterConfigurationService.announceSaving(false);
  }


  filterJobTitle(jobRows: ExpandableListItem[], jobTitle: JobTitle, includedInRatio: boolean = false, isFixed: boolean = null): JobTitle {
    var row = jobRows.find(j => j.id == jobTitle.id);
    if (!includedInRatio && isFixed == null && row) {
      return jobTitle;
    }
    else if (row) {
      jobTitle.order = row.order;
      if (includedInRatio) {
        var ratioCell = row.cells.some(n => n.id == "includedInRatio" && n.value);
        if (ratioCell) {
          return jobTitle;
        }
      }
      else if (isFixed) {
        var fixedCell = row.cells.some(n => n.id == "isFixed" && n.value == "Fixed");
        if (fixedCell) {
          return jobTitle;
        }
      }
      else if (!isFixed) {
        var variableCell = row.cells.some(n => n.id == "isFixed" && n.value == "Variable");
        if (variableCell) {
          return jobTitle;
        }
      }
    }
  }

  private ValidateGeneralTab(): boolean {
    if(this.jobTitleRows.length == 0 || this.measureRows.length > this.maximumAllowedMeasures || this.varianceRows.length == 0
      || this.selectedFacility === undefined
      || this.selectedName === "" || this.selectedName === undefined || this.hasMissingJobType
      || this.selectedNumber === "" || this.selectedNumber === undefined)
      return false;
    else
      return true;
  }

  get hasMissingJobType(): boolean{
    let requiredFlag = this.jobTitleRows.some(n=>n.cells.some(c=>c.type=='list' && c.value==""));
    if(requiredFlag){
      this._costCenterConfigurationService.announceError("Please fill out a type for each job title.");
    }
    else{
      this._costCenterConfigurationService.clearError();
    }
    return requiredFlag;
  }

  get tooManyMeasures() : boolean {
    return this.selectedMeasures 
        && this.selectedMeasures.length > this.maximumAllowedMeasures
  }

  get missingJobTitles() : boolean {
    return (!this.isFutureShiftBlockAvailable || this.isNewCostCenter) && (!this.selectedJobTitles || !MiscUtil.isValidPositiveNumber(this.selectedJobTitles.length));
  }

  get missingVarianceReasons() : boolean {
    return !this.selectedVarianceReasons || !MiscUtil.isValidPositiveNumber(this.selectedVarianceReasons.length);        
  }

  get isValid() : boolean {
    return !this.tooManyMeasures
        && !this.missingJobTitles
        && !this.missingVarianceReasons;
  }
}
